import type { EditorAPI } from '#packages/editorAPI';
import type { EditorState } from '#packages/stateManagement';
import type {
  CurrentTpaInnerRoutesInfo,
  IgnoreSubPagesFeature,
  SubPagesToIgnore,
  TpaInnerRouteData,
} from './tpaDynamicPagesReducer';
import _ from 'lodash';

const getSelectedRouteInfo = (state: EditorState): TpaInnerRouteData =>
  state.tpaDynamicPages.selectedInnerRoute;

const getCurrentRouteInfo = (state: EditorState): CurrentTpaInnerRoutesInfo =>
  state.tpaDynamicPages.currentTpaInfo;

const getInnerRoutesCounts = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
) => state.tpaDynamicPages.innerRoutesCounts?.[appDefinitionId]?.[pageId];
import { WIX_BLOG } from '@wix/app-definition-ids';

const filterRouteDataByLanguage = (
  editorAPI: EditorAPI,
  routeData: TpaInnerRouteData[],
) => {
  const isMultilingual = editorAPI?.dsRead?.language?.multilingual?.isEnabled();
  const language = editorAPI.dsRead.language.current?.get();

  if (!isMultilingual || !language) {
    return routeData;
  }

  return routeData?.filter((item) => {
    const languageCode = item?.metadata?.language?.code;
    return !languageCode || languageCode === language;
  });
};

const withFilterAndOrder = (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  routeData: TpaInnerRouteData[],
): TpaInnerRouteData[] => {
  if (appDefinitionId !== WIX_BLOG) {
    return routeData || [];
  }

  return _.sortBy(filterRouteDataByLanguage(editorAPI, routeData), 'itemId');
};

const getAllSubPagesInnerRoutes = function (
  editorAPI: EditorAPI,
  state: Partial<EditorState>,
  appDefinitionId: string,
  pageId: string,
): TpaInnerRouteData[] {
  const routeData = Object.values(
    state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
      ?.subPages || {},
  )
    .filter((subPageInnerRoutes) => !!subPageInnerRoutes)
    .flat(1);
  return withFilterAndOrder(editorAPI, appDefinitionId, routeData);
};

const getFocusedSubPages = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  feature: IgnoreSubPagesFeature,
): string[] => {
  const allSubPages = Object.keys(
    state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
      ?.subPages ?? {},
  );
  const disabledSubPagesInFeature =
    state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
      ?.subPagesToIgnore?.[feature] ?? [];
  return allSubPages.filter(
    (subPage) => !disabledSubPagesInFeature.includes(subPage),
  );
};

const getAllCachedInnerRoutes = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  featureDefinition: string[],
) =>
  Object.entries(
    state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
      ?.subPages ?? [],
  ).reduce(
    (
      acc: TpaInnerRouteData[],
      [subPage, innerRoutes]: [string, TpaInnerRouteData[]],
    ) => {
      if (!featureDefinition.includes(subPage) && innerRoutes?.length > 0) {
        acc = acc.concat(innerRoutes);
      }
      return acc;
    },
    [],
  );

const getFocusedInnerRoutes = (
  editorAPI: EditorAPI,
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  feature: IgnoreSubPagesFeature,
): TpaInnerRouteData[] => {
  const featureDefinition =
    state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
      ?.subPagesToIgnore?.[feature];

  const routeData: TpaInnerRouteData[] =
    featureDefinition && featureDefinition.length > 0
      ? getAllCachedInnerRoutes(
          state,
          appDefinitionId,
          pageId,
          featureDefinition,
        )
      : getAllSubPagesInnerRoutes(editorAPI, state, appDefinitionId, pageId);
  return withFilterAndOrder(editorAPI, appDefinitionId, routeData);
};

const getSubPageInnerRoute = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  subPage: string,
) =>
  state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
    ?.subPages?.[subPage];

const getSubPagesFailedToFetch = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
): string[] =>
  state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
    ?.subPages
    ? Object.entries(
        state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[
          pageId
        ].subPages,
      ).reduce((acc, [subPageName, innerRoutes]) => {
        if (!innerRoutes) {
          acc.push(subPageName);
        }
        return acc;
      }, [])
    : null;

const getSubPagesToIgnore = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
): SubPagesToIgnore =>
  state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
    ?.subPagesToIgnore;

const isInFetchProcess = (state: EditorState) =>
  state.tpaDynamicPages.inInnerRoutesFetchProcess;

const getCurrentPageSessionId = (state: EditorState) =>
  state.tpaDynamicPages.currentPageSessionId;

export {
  getSelectedRouteInfo,
  getCurrentRouteInfo,
  getAllSubPagesInnerRoutes,
  getSubPagesFailedToFetch,
  getInnerRoutesCounts,
  getFocusedSubPages,
  getFocusedInnerRoutes,
  getSubPageInnerRoute,
  isInFetchProcess,
  getCurrentPageSessionId,
  getSubPagesToIgnore,
};
